/* eslint-disable testing-library/no-debugging-utils -- disabling debug warning */
import { FullStory, init as fullStoryInit } from '@fullstory/browser'
import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { type Config } from '../../config/config'

// Types for FullStory SDK configuration
interface FullStoryConfig {
  orgId: string
  enableLogging?: boolean
}

declare global {
  interface Window {
    evergreenFullStory?: {
      initializeFullStorySDK: (_config: FullStoryConfig) => void
      identifyUser: (_userId: string, _traits?: Record<string, unknown>) => void
      trackEvent: (_eventName: string, _properties?: Record<string, unknown>) => void
      isReady: () => boolean
    }
  }
}

const log = new Logger('Screen Recording Service - Fullstory')

let instance: ScreenRecordingService | null = null

class ScreenRecordingService {
  private isInitialized = false
  private userId: string | undefined
  private jobQueue: (() => void)[] = []
  private sessionUrl: string | undefined // Fullstory session url

  constructor() {
    if (!instance) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias -- Singleton pattern
      instance = this

      // Expose wrapper methods on separate namespace for RudderStack to invoke
      if (typeof window !== 'undefined') {
        window.evergreenFullStory = {
          initializeFullStorySDK: this.initializeFullStorySDK,
          identifyUser: this.identifyUser,
          trackEvent: this.trackEvent,
          isReady: this.getIsReady,
        }

        log.debug('window.evergreenFullStory methods exposed for RudderStack integration')
      }

      // Notifications linsteners
      const loggerDebugHandler = (data: unknown): void => {
        const logs = (data as { detail: unknown }).detail
        this.enqueue(() => {
          FullStory('log', { level: 'debug', msg: logs })
        })
      }
      notifications.on(NOTIFICATIONS.LOGGER.DEBUG, loggerDebugHandler)

      const loggerInfoHandler = (data: unknown): void => {
        const logs = (data as { detail: unknown }).detail
        this.enqueue(() => {
          FullStory('log', { level: 'log', msg: logs })
        })
      }
      notifications.on(NOTIFICATIONS.LOGGER.INFO, loggerInfoHandler)

      const loggerWarnHandler = (data: unknown): void => {
        const logs = (data as { detail: unknown }).detail
        this.enqueue(() => {
          FullStory('log', { level: 'warn', msg: logs })
        })
      }
      notifications.on(NOTIFICATIONS.LOGGER.WARN, loggerWarnHandler)

      const loggerErrorHandler = (data: unknown): void => {
        const logs = (data as { detail: unknown }).detail
        this.enqueue(() => {
          FullStory('log', { level: 'error', msg: logs })
        })
      }
      notifications.on(NOTIFICATIONS.LOGGER.ERROR, loggerErrorHandler)

      // Notifications listeners - on sign out
      const signOutListener = (): void => {
        if (this.userId) {
          this.userId = undefined
          this.enqueue(() => {
            log.debug('Update identity - anonymous')
            FullStory('setIdentity', { anonymous: true })
          })
        }
      }
      notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, signOutListener)
    }
  }

  private enqueue(action: () => void): void {
    if (this.isInitialized && typeof window !== 'undefined') {
      action()
    } else {
      this.jobQueue.push(action)
    }
  }

  private flushQueue(): void {
    while (this.jobQueue.length > 0) {
      const action = this.jobQueue.shift()
      action?.()
    }
  }

  // Getter for the session url
  public getSessionUrl = (): string | undefined => {
    return this.sessionUrl
  }

  /**
   * Check if FullStory is initialized and ready to process events
   * Used by RudderStack custom integration via window.evergreenFullStory.isReady()
   */
  public getIsReady = (): boolean => {
    return this.isInitialized
  }

  /**
   * Initialize FullStory SDK
   * Called by RudderStack custom integration via window.evergreenFullStory.initializeFullStorySDK()
   *
   * @param config - FullStory configuration with orgId and optional enableLogging
   */
  public initializeFullStorySDK = (config: FullStoryConfig): void => {
    log.debug('initializeFullStorySDK called via window.evergreenFullStory', config)

    if (this.isInitialized) {
      log.warn('FullStory SDK already initialized, skipping re-initialization')
      return
    }

    const initCallback = (d: unknown): void => {
      this.isInitialized = true

      const data = d as { sessionUrl: string }

      log.info('FullStory is initialized via RudderStack custom integration')
      log.info('FullStory session url', data.sessionUrl)

      this.sessionUrl = data.sessionUrl

      // Send notification to other services
      notifications.dispatch(NOTIFICATIONS.FULLSTORY.INIT, {
        sessionUrl: data.sessionUrl,
      })

      if (!this.userId) {
        FullStory('setIdentity', { anonymous: true })
      }

      this.flushQueue()
    }

    try {
      fullStoryInit(
        {
          orgId: config.orgId,
          devMode: config.enableLogging ?? false,
          appHost: 'app.fullstory.com',
        },
        initCallback,
      )

      log.info('FullStory SDK initialization started')
    } catch (error) {
      log.error('Failed to initialize FullStory SDK', error)
    }
  }

  /**
   * Identify a user in FullStory
   * Called by RudderStack custom integration via window.evergreenFullStory.identifyUser()
   * Note: PII is already stripped by the custom integration before this is called
   *
   * @param userId - User ID
   * @param traits - User traits (PII already stripped by custom integration)
   */
  public identifyUser = (userId: string, traits?: Record<string, unknown>): void => {
    log.debug('identifyUser called via window.evergreenFullStory', { userId, traits })

    if (this.userId !== userId) {
      this.userId = userId
    }

    this.enqueue(() => {
      log.debug('Setting user identity in FullStory', { userId })

      // Set user identity
      FullStory('setIdentity', {
        uid: userId,
        properties: traits,
      })

      log.info('User identified in FullStory', { userId })
    })
  }

  /**
   * Track a custom event in FullStory
   * Called by RudderStack custom integration via window.evergreenFullStory.trackEvent()
   * Note: PII is already stripped by the custom integration before this is called
   *
   * @param eventName - Event name
   * @param properties - Event properties (PII already stripped by custom integration)
   */
  public trackEvent = (eventName: string, properties?: Record<string, unknown>): void => {
    log.debug('trackEvent called via window.evergreenFullStory', { eventName, properties })

    this.enqueue(() => {
      log.debug('Tracking event in FullStory', { eventName, properties })

      FullStory('trackEvent', {
        name: eventName,
        properties: properties ?? {},
      })

      log.info('Event tracked in FullStory', { eventName })
    })
  }

  /**
   * Legacy init method for backward compatibility
   * This method is kept for existing code that may still use it
   * New integrations should use initializeFullStorySDK() via window.evergreenFullStory
   */
  init = (config: Config): void => {
    if (typeof config.fullStory === 'undefined') {
      log.error('FullStory configuration is missing')
      throw new Error('FullStory configuration is required')
    }
    const initCallback = (d: unknown): void => {
      this.isInitialized = true

      const data = d as { sessionUrl: string }

      log.info('FullStory is initialized')
      log.info('FullStory session url', data.sessionUrl)

      this.sessionUrl = data.sessionUrl

      // Send notification to other services
      notifications.dispatch(NOTIFICATIONS.FULLSTORY.INIT, {
        sessionUrl: data.sessionUrl,
      })

      if (!this.userId) {
        FullStory('setIdentity', { anonymous: true })
      }

      this.flushQueue()
    }

    fullStoryInit({ ...config.fullStory, appHost: 'app.fullstory.com' }, initCallback)
  }
}

export const screenRecordingService = new ScreenRecordingService()
